import {Component, DoCheck, EventEmitter, Host, Input, OnDestroy, OnInit, Optional, Output, SkipSelf, ViewChild} from '@angular/core';
import {AbstractControl, ControlContainer, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Subscription} from 'rxjs/Subscription';
import {coerceBooleanProperty, coerceNumberProperty} from '@angular/cdk/coercion';
import {InputManagerDirective} from '@utils/input-manager.directive';

@Component({
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: FormInputComponent,
    }
  ],
  selector: 'app-form-input',
  templateUrl: './form-input.component.html',
  styleUrls: ['./form-input.component.scss', '../form-components-styles.scss']
})

export class FormInputComponent implements ControlValueAccessor, OnInit, OnDestroy, DoCheck {

  // Label del Input
  @Input() label = '';

  // Mantener el espacio del label, desactivar para casos donde no se requiere label
  _keepLabelSpace = true;
  @Input() set keepLabelSpace(val: boolean) {
    this._keepLabelSpace = coerceBooleanProperty(val);
  }

  // Placeholder del Input
  @Input() placeholder = '';

  // Mensaje de error Inferior (solo acepta uno, podria prepararse una property para un array y mostrar en vertical)
  @Input() errorMessage = '';

  // Mantener el espacio del error, desactivar si no hay validaciones (mantener en formularios para evitar conflictos de tamaño de inputs)
  _keepErrorSpace = true;
  @Input() set keepErrorSpace(val: boolean) {
    this._keepErrorSpace = coerceBooleanProperty(val);
  }

  // Icono de google (fonts.google.com/icons)
  @Input() googleIconName = '';

  // Icono SVG local
  @Input() svgIconName = '';

  // Mostrar el boton X para eliminar
  _allowClearButton = false;
  @Input() set allowClearButton(val: boolean) {
    this._allowClearButton = coerceBooleanProperty(val);
  }

  // Mostrar un spinner en lugar del icono (se recomienda deshabilitar el componente)
  _loading = false;
  @Input() set loading(val: boolean) {
    this._loading = coerceBooleanProperty(val);
  }

  @Input() labelSize: 'sm' | 'md' = 'sm';

  // InputManager

  // Filtro de texto
  @Input() IM_textFilter: 'num-only' | 'num-decimals' | 'cuit' | 'ignore' | 'preventAll' | 'num-decimals-comma' = 'ignore';

  // Separador de miles
  @Input() IM_thousandsSeparator = false;

  @Input() IM_disablePaste = false;

  @ViewChild(InputManagerDirective) inputManager: InputManagerDirective;

  // Forzar autofocus
  _IM_autofocus = false;
  @Input() set IM_autofocus(val: boolean) {
    this._IM_autofocus = coerceBooleanProperty(val);
  }

  // En caso de Blur, forzar nuevamente el focus 'x' veces (generalmente uno es suficiente)
  _IM_forceAutofocusAttempts = 0;
  @Input() set IM_forceAutofocusAttempts(val: number) {
    this._IM_forceAutofocusAttempts = coerceNumberProperty(val);
  }

  // Para numeros, maximo lenght sin contar puntos ni comas
  _IM_maxLength = 0;
  @Input() set IM_maxLength(val: number) {
    this._IM_maxLength = val;
  }

  // Para texto, lenght maximo del string
  _IM_maxLengthText = 0;
  @Input() set IM_maxLengthText(val: number) {
    this._IM_maxLengthText = coerceNumberProperty(val);
  }

  _IM_AutofillAmount = 0;
  @Input() set IM_AutofillAmount(val: number) {
    this._IM_AutofillAmount = val;
  }

  // Evento al presionar Enter
  @Output() enterKeyPressed = new EventEmitter();

  // Evento al pegar
  @Output() formPaste = new EventEmitter<ClipboardEvent>();

  // Atributos de Form

  // Valor del input (posee NgModel)
  value = '';

  // Flag Touched
  touched = false;

  // Flag Disabled
  disabled = false;

  // Flag Control Valido
  validStatus = false;

  // Control del Form
  private control: AbstractControl;
  // Subscripcion al estado Valid
  private statusSubscription: Subscription = new Subscription();

  // Hacky, levantamos el control usando el mismo nombre de property que se usa para acceder al form
  @Input() formControlName: string;

  // Callback al cambiar el valor del input
  onChange = (val) => {};

  // Callback al cambiar a estado Touched
  onTouched = () => {};

  // En el constructor obtenemos el formGroup al cual pertenece el input
  constructor(
    @Optional() @Host() @SkipSelf()
    private controlContainer: ControlContainer
  ) { }

  ngOnInit() {
    this.initializeFormControl();
  }

  ngOnDestroy() {
    this.statusSubscription.unsubscribe();
  }

  ngDoCheck() {
    if (this.control && this.control.touched && !this.touched) {
      this.markAsTouched();
    }
  }

  initializeFormControl() {
    if (!this.controlContainer) {
      console.warn('Can\'t find parent FormGroup directive');
      return;
    }

    if (!this.formControlName) {
      console.warn('Missing FormControlName directive from host element of the component');
      return;
    }

    this.control = this.controlContainer.control.get(this.formControlName);
    this.validStatus = this.control.valid;
    this.statusSubscription = this.control.statusChanges.subscribe(x => {
      this.validStatus = !this.control.invalid; // usamos invalid pues valid posee un bug
    });
  }

  handlePaste(pasteEvent: ClipboardEvent, input: HTMLInputElement) {
    // aparentemente la validacion y el detectionchanges no se disparan al pegar texto, asi que lo disparamos manualmente
    // dado que en el clipboard puede haber cualquier cosa, solo hacemos esto si es un string
    if (input.value.length) {
      this.control.setValue(input.value, { emitEvent: true });
    }
    this.formPaste.emit(pasteEvent);
  }

  // cuando hay un cambio en el input, llamamos a la callbackFunction onchange para que lo registre el form
  modelChanged() {
    this.onChange(this.value);
  }

  // Eliminar el contenido del Input
  clearValue() {
    if (this.disabled) {
      return;
    }
    this.value = '';
    this.modelChanged();
  }

  /** En caso de que se quiera modificar el valor por codigo
   * mediante AbstractControl.setValue(value) se llamará a este método
   * */
  writeValue(val: string) {
    this.value = val;
    this.inputManager.onBlur();
  }

  /** El formulario registrara esta callbackFunction para notificar cuando hubo un
   * cambio en el formulario del lado del usuario
   * */
  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  /** El formulario registrara esta callbackFunction para notificar el estado Touched
   * */
  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  /** El formulario podra usar esta funcion para deshabilitar el control
   * */
  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  // Marcamos el control como Touched
  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }
}



// WEBPACK FOOTER //
// E:/jenkins_home/workspace/_agenda-unco-front-legacy_master/frontend-web/src/app/components/common/form-components/form-input/form-input.component.ts